import { secondsToFormattedTime } from './dateTimeFormatUtils';
import { getSpeakerInfo } from './transcriptUtils';
import { USER, CONTACT, COMPANY, UNKNOWN_PARTICIPANT } from '../constants/CallParticipantTypes';
import I18n from 'I18n';
/**
 * Groups participants by type in a single pass for better performance
 */
const groupParticipantsByType = transcriptParticipants => {
  return transcriptParticipants.reduce((acc, participant) => {
    // Defensive check for null/undefined participantName
    if (!participant.participantName) {
      return acc;
    }
    switch (participant.participantType) {
      case USER:
        acc.hubspot.push(participant);
        break;
      case CONTACT:
        acc.contacts.push(participant);
        break;
      case COMPANY:
        acc.companies.push(participant);
        break;
      case UNKNOWN_PARTICIPANT:
        acc.unknown.push(participant);
        break;
      default:
        acc.other.push(participant);
    }
    return acc;
  }, {
    hubspot: [],
    contacts: [],
    companies: [],
    unknown: [],
    other: []
  });
};

/**
 * Formats participant overview section with grouped and sorted participants
 * Uses markdown formatting for better AI/LLM parsing
 */
const formatParticipantOverview = transcriptParticipants => {
  if (!transcriptParticipants || transcriptParticipants.length === 0) {
    return '';
  }
  const grouped = groupParticipantsByType(transcriptParticipants);
  const sections = [`# ${I18n.text('recordingReviewComponents.transcript.copyTranscript.participantsHeader')}`, ''];

  // Add HubSpot section (sorted alphabetically)
  if (grouped.hubspot.length > 0) {
    sections.push(`## ${I18n.text('recordingReviewComponents.transcript.copyTranscript.hubspotLabel')}`);
    grouped.hubspot.sort((a, b) => a.participantName.localeCompare(b.participantName)).forEach(p => {
      sections.push(`- ${p.participantName}`);
    });
    sections.push('');
  }

  // Add Contacts (sorted alphabetically)
  if (grouped.contacts.length > 0) {
    sections.push(`## ${I18n.text('recordingReviewComponents.transcript.copyTranscript.contactLabel')}`);
    grouped.contacts.sort((a, b) => a.participantName.localeCompare(b.participantName)).forEach(p => {
      sections.push(`- ${p.participantName}`);
    });
    sections.push('');
  }

  // Add Companies (sorted alphabetically)
  if (grouped.companies.length > 0) {
    sections.push(`## ${I18n.text('recordingReviewComponents.transcript.copyTranscript.companyLabel')}`);
    grouped.companies.sort((a, b) => a.participantName.localeCompare(b.participantName)).forEach(p => {
      sections.push(`- ${p.participantName}`);
    });
    sections.push('');
  }

  // Add unknown participants (sorted for consistency)
  if (grouped.unknown.length > 0) {
    grouped.unknown.sort((a, b) => a.participantName.localeCompare(b.participantName)).forEach(p => {
      sections.push(`- ${p.participantName}`);
    });
    sections.push('');
  }

  // Add other participants (BOT, etc.) with labels
  if (grouped.other.length > 0) {
    grouped.other.sort((a, b) => a.participantName.localeCompare(b.participantName)).forEach(p => {
      sections.push(`## ${p.participantType}`);
      sections.push(`- ${p.participantName}`);
      sections.push('');
    });
  }
  return sections.join('\n');
};

/**
 * Formats transcript utterances and participants for clipboard copying
 * Returns a formatted string with participant overview followed by transcript content
 */
export const formatTranscriptForClipboard = (transcriptUtterances, transcriptParticipants) => {
  const participantOverview = formatParticipantOverview(transcriptParticipants);
  const transcriptContent = transcriptUtterances.map(utterance => {
    const {
      participantName
    } = getSpeakerInfo(utterance, transcriptParticipants);

    // Skip utterances with null/undefined participant names
    if (!participantName) {
      return null;
    }
    const timestamp = secondsToFormattedTime(utterance.startTimeSeconds);

    // Use markdown bold for participant names for better AI parsing
    return `**${participantName}**${timestamp ? ` (${timestamp})` : ''}: ${utterance.utterance.trim()}`.trim();
  }).filter(line => line !== null).join('\n');

  // Combine participant overview with transcript using markdown separator
  if (participantOverview && transcriptContent) {
    const transcriptHeader = I18n.text('recordingReviewComponents.transcript.copyTranscript.transcriptHeader');
    return `${participantOverview}\n---\n\n# ${transcriptHeader}\n\n${transcriptContent}`;
  } else if (participantOverview) {
    return participantOverview;
  }
  return transcriptContent;
};